import { useState, useEffect } from "react";
import {
  FileText, Upload, File, Download,
  LoaderPinwheel, FolderOpen, CalendarDays,
} from "lucide-react";
import ModalSubirDocumento from "../../../components/modals/ModalSubirDocumento";
import { toast } from "react-toastify";
import axios from "axios";
import { hasPermission } from "../../../utils/permissions";

const iconoPorTipo = (mime) => {
  if (!mime) return <File className="h-5 w-5 text-gray-400" />;
  if (mime.includes("pdf")) return <FileText className="h-5 w-5 text-red-500" />;
  if (mime.includes("image")) return <File className="h-5 w-5 text-blue-500" />;
  return <File className="h-5 w-5 text-gray-400" />;
};

const Documentos = ({
  equipo,
  publicView = false,
  initialDocumentos = [],
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [documentos, setDocumentos] = useState(initialDocumentos || []);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_API_URL;

  const cargarDocumentos = async () => {
    if (publicView) {
      setDocumentos(initialDocumentos || []);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/equipos/docs/${equipo._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocumentos(data);
    } catch {
      toast.error("No se pudo cargar los documentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDocumentos();
    // eslint-disable-next-line
  }, [publicView, JSON.stringify(initialDocumentos)]);

  const handleRefresh = () => {
    cargarDocumentos();
  };

  return (
    <div className="space-y-5">
      <ModalSubirDocumento
        open={openModal}
        onClose={() => setOpenModal(false)}
        equipoId={equipo._id}
        onUploaded={handleRefresh}
      />

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <FileText className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              Documentación del equipo
            </h3>
            <p className="text-xs text-gray-400">
              {documentos.length} documento{documentos.length !== 1 ? "s" : ""} · {equipo.codigo}
            </p>
          </div>
        </div>

        {!publicView && hasPermission("equipos.editar") && (
          <button
            onClick={() => setOpenModal(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <Upload className="h-4 w-4" />
            Subir documento
          </button>
        )}
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm text-blue-700">
          <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
          Cargando documentos…
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && documentos.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-200 py-12 text-center">
          <FolderOpen className="h-9 w-9 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Sin documentos cargados</p>
          {!publicView && hasPermission("equipos.editar") && (
            <p className="text-xs text-gray-400 mt-1">
              Sube manuales o documentos técnicos del equipo
            </p>
          )}
        </div>
      )}

      {/* ── Document list ── */}
      {!loading && documentos.length > 0 && (
        <div className="space-y-2">
          {documentos.map((doc) => (
            <div
              key={doc._id}
              className="flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-white px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50">
                  {iconoPorTipo(doc.mimeType)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {doc.tipoDocumento?.nombre || doc.tipo}
                  </p>
                  <div className="flex flex-wrap gap-x-3 text-xs text-gray-400 mt-0.5">
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />
                      {new Date(doc.fechaDocumento).toLocaleDateString()}
                    </span>
                    {doc.fechaExpedicion && (
                      <span>
                        Expedición: {new Date(doc.fechaExpedicion).toLocaleDateString()}
                      </span>
                    )}
                    {doc.fechaVencimiento && (
                      <span>
                        Vence: {new Date(doc.fechaVencimiento).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <a href={doc.url} target="_blank" rel="noreferrer" className="shrink-0">
                <button className="inline-flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-colors">
                  <Download className="h-3.5 w-3.5" />
                  Descargar
                </button>
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Documentos;
